/**
 * useTransactionFieldUsage Hook
 *
 * Computes which custom field IDs are in use per transaction type (expense/income/transfer).
 * Used to show "In use" and to block delete when a field is in use (only allow hide).
 */

import { useEffect, useState, useCallback } from "react";
import { db } from "../../../firebase";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import type {
  TransactionCustomFieldScopeType,
  TransactionCustomFieldValues,
} from "../../../types";
import type { Transaction } from "../../../types";

export interface FieldUsageByType {
  expense: number;
  income: number;
  transfer: number;
}

const usageCache = new Map<string, { data: Record<string, FieldUsageByType>; timestamp: number }>();
const CACHE_TTL_MS = 90 * 1000;

export interface UseTransactionFieldUsageParams {
  workspaceId: string;
}

export interface UseTransactionFieldUsageResult {
  /** fieldId -> { expense, income, transfer } counts */
  fieldUsageByType: Record<string, FieldUsageByType>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const USAGE_QUERY_LIMIT = 10000;

export function useTransactionFieldUsage({
  workspaceId,
}: UseTransactionFieldUsageParams): UseTransactionFieldUsageResult {
  const cached = workspaceId ? usageCache.get(workspaceId) : null;
  const hasValidCache = !!(cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS);

  const [fieldUsageByType, setFieldUsageByType] = useState<
    Record<string, FieldUsageByType>
  >(hasValidCache && cached ? cached.data : {});
  const [loading, setLoading] = useState(!hasValidCache);
  const [error, setError] = useState<string | null>(null);

  const runQuery = useCallback(async () => {
    if (!workspaceId) {
      setFieldUsageByType({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const transactionsRef = collection(
        db,
        "workspaces",
        workspaceId,
        "transactions"
      );
      const q = query(
        transactionsRef,
        orderBy("createdAt", "desc"),
        limit(USAGE_QUERY_LIMIT)
      );
      const snapshot = await getDocs(q);
      const usage: Record<string, FieldUsageByType> = {};

      snapshot.docs.forEach((docSnap) => {
        const t = docSnap.data() as Transaction;
        const type = t.type as TransactionCustomFieldScopeType | undefined;
        if (
          type !== "expense" &&
          type !== "income" &&
          type !== "transfer"
        ) {
          return;
        }
        const customFieldValues = t.customFieldValues as
          | TransactionCustomFieldValues
          | undefined;
        if (!customFieldValues || typeof customFieldValues !== "object") {
          return;
        }
        const fieldIds = Object.keys(customFieldValues);
        fieldIds.forEach((fieldId) => {
          const val = customFieldValues[fieldId];
          if (val === undefined || val === null) return;
          const str =
            typeof val === "string" ? val : (val as { url?: string })?.url;
          if (str === undefined || str === "") return;
          if (!usage[fieldId]) {
            usage[fieldId] = { expense: 0, income: 0, transfer: 0 };
          }
          usage[fieldId][type] += 1;
        });
      });

      if (workspaceId) usageCache.set(workspaceId, { data: usage, timestamp: Date.now() });
      setFieldUsageByType(usage);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to load field usage";
      setError(message);
      setFieldUsageByType({});
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    runQuery();
  }, [runQuery]);

  return {
    fieldUsageByType,
    loading,
    error,
    refresh: runQuery,
  };
}
